/**
 * B431 / B432 - the one lock every sheet and dialog takes so the page behind it cannot scroll.
 *
 * On the web the lock is on the ROOT element (`html`), never on `body`. `global.css` gives `html` and `body`
 * `height: 100%` and `overflow-x: clip`; `overflow: hidden` on `body` then makes body a scroll container exactly
 * one viewport tall, the document stops being tall, and the browser clamps the page to the top for as long as the
 * sheet is open (measured in Chrome: 3324 before, 0 open). On `html` the viewport itself stops scrolling for the
 * user (wheel, touch, keys) and its offset is left alone, so nothing needs saving or restoring.
 * `scrollbar-gutter: stable` keeps the width a classic scrollbar took, so the page does not widen by that much while
 * a sheet is open (overlay scrollbars and phones have none, and nothing changes there).
 *
 * In the native app (Capacitor shell, `isNativeApp()`) the lock is what each sheet did before B432: `overflow:
 * hidden` on `body`, nothing else. The keyboard plugin and WKWebView have their own rakes (product CLAUDE.md), and a
 * native change rides the next iOS build, so this module does not move the native lock.
 *
 * Counted, so two sheets that overlap or follow each other never leave the lock behind or lift it early: the first
 * takes the target's own inline values, the last one puts them back. The release is idempotent.
 */

import { isNativeApp } from './platform';

interface Held {
  el: HTMLElement;
  saved: Array<[name: string, value: string]>;
  /** The web lock leaves no empty `style=""` on `html`; the native body lock never did that cleanup. */
  dropEmptyStyle: boolean;
}

let locks = 0;
let held: Held | null = null;

export function lockPageScroll(): () => void {
  if (locks++ === 0) {
    const native = isNativeApp();
    const el = native ? document.body : document.documentElement;
    const lock: Array<[string, string]> = native
      ? [['overflow', 'hidden']]
      : [
          ['overflow', 'hidden'],
          ['scrollbar-gutter', 'stable'],
        ];
    held = {
      el,
      saved: lock.map(([name]) => [name, el.style.getPropertyValue(name)]),
      dropEmptyStyle: !native,
    };
    for (const [name, value] of lock) el.style.setProperty(name, value);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--locks > 0 || held === null) return;
    const { el, saved, dropEmptyStyle } = held;
    held = null;
    for (const [name, value] of saved) {
      if (value === '') el.style.removeProperty(name);
      else el.style.setProperty(name, value);
    }
    if (dropEmptyStyle && el.getAttribute('style') === '') el.removeAttribute('style');
  };
}
